import * as React from "react";
import type { DefaultTooltipContentProps } from "recharts";
import * as RechartsPrimitive from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { isBriefingPdfSnapshot } from "@/lib/briefing/briefingUtils";
import { cn } from "@/lib/utils";

/**
 * Segment used in `var(--color-${...})` must be a valid CSS identifier (no spaces).
 * {@link ChartConfig} keys stay as-is for lookups; this only affects injected `--color-*` names.
 */
export const chartColorCssVarKey = (key: string): string => {
  const s = key.replace(/[^a-zA-Z0-9_-]/g, "_").replace(/_+/g, "_");
  return s.length > 0 ? s : "chart";
};

/** Maps theme key to CSS selector prefix for injected chart color variables. */
const THEMES = { light: "", dark: ".dark" } as const;

const INITIAL_DIMENSION = { width: 320, height: 200 } as const;

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
>;

interface ChartContextProps {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextProps | null>(null);

const useChart = () => {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
};

type RechartsChildProps = {
  isAnimationActive?: boolean;
  children?: React.ReactNode;
};

/** Forces `isAnimationActive={false}` on Recharts series under a PDF snapshot tree. */
const disableChartEnterAnimation = (
  children: React.ReactNode,
): React.ReactNode =>
  React.Children.map(children, (child) => {
    if (!React.isValidElement<RechartsChildProps>(child)) {
      return child;
    }

    const nestedChildren =
      child.props.children !== undefined
        ? disableChartEnterAnimation(child.props.children)
        : child.props.children;

    const hasAnimationProp = child.props.isAnimationActive !== undefined;
    const hasNestedChange = nestedChildren !== child.props.children;

    if (!hasAnimationProp && !hasNestedChange) {
      return child;
    }

    return React.cloneElement(child, {
      ...(hasAnimationProp ? { isAnimationActive: false } : {}),
      ...(hasNestedChange ? { children: nestedChildren } : {}),
    });
  });

const ChartContainer = ({
  id,
  className,
  children,
  config,
  initialDimension = INITIAL_DIMENSION,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >["children"];
  initialDimension?: {
    width: number;
    height: number;
  };
}) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`;
  const isPdfSnapshot = isBriefingPdfSnapshot();
  const chartChildren = isPdfSnapshot
    ? disableChartEnterAnimation(children)
    : children;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          // w-full min-w-0: flex/grid parents often collapse width without this (Recharts #172).
          // min-h: ResponsiveContainer measures the parent; 0×0 yields width/height -1 and console warnings.
          // Do not default aspect-video here — polar charts (e.g. PieDonutChart) set aspect-square via className.
          "flex w-full min-w-0 min-h-[200px] justify-center text-xxs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer
          height="100%"
          initialDimension={initialDimension}
          minHeight={200}
          minWidth={0}
          width="100%"
        >
          {chartChildren}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
};

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, itemConfig]) => itemConfig.theme ?? itemConfig.color,
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      // biome-ignore lint/security/noDangerouslySetInnerHtml: injects theme CSS variables from ChartConfig keys/colors only (shadcn chart pattern), not user HTML.
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ??
      itemConfig.color;
    return color ? `  --color-${chartColorCssVarKey(key)}: ${color};` : null;
  })
  .join("\n")}
}
`,
          )
          .join("\n"),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

/**
 * Resolves a swatch/tooltip color from {@link ChartConfig}: plain `color`, or
 * `var(--color-*)` when only `theme` is set (matches {@link ChartStyle} injection).
 */
const resolveChartColorFromConfig = (
  itemConfig: ChartConfig[string] | undefined,
  datumKey: string,
): string | undefined => {
  if (!itemConfig) return undefined;
  if (typeof itemConfig.color === "string") return itemConfig.color;
  if (itemConfig.theme) {
    return `var(--color-${chartColorCssVarKey(datumKey)})`;
  }
  return undefined;
};

/**
 * Key into {@link ChartConfig} for a tooltip row: value at `fieldKey` on the datum
 * (e.g. `payload.sentiment` when `fieldKey` is `"sentiment"` for Pie), else Recharts `name` / `dataKey`.
 */
const resolveChartConfigDatumKey = (
  item: {
    name?: unknown;
    dataKey?: unknown;
    payload?: unknown;
  },
  fieldKey: string | undefined,
): string => {
  if (
    fieldKey != null &&
    fieldKey !== "" &&
    item.payload != null &&
    typeof item.payload === "object"
  ) {
    const v = (item.payload as Record<string, unknown>)[fieldKey];
    if (v != null && v !== "") return String(v);
  }
  const fallback = item.name ?? item.dataKey ?? "value";
  return String(fallback ?? "value");
};

const ChartTooltipContent = ({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
  compact = false,
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
    indicator?: "line" | "dot" | "dashed";
    nameKey?: string;
    labelKey?: string;
    /**
     * Single horizontal row: swatch + segment name + value (no separate header).
     * Intended for pie/donut tooltips.
     */
    compact?: boolean;
  } & Omit<
    DefaultTooltipContentProps<ValueType, NameType>,
    "accessibilityLayer"
  >) => {
  const { config } = useChart();

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || compact || !payload?.length) {
      return null;
    }

    const item = payload[0];
    if (!item) return null;
    const key = resolveChartConfigDatumKey(item, labelKey ?? nameKey);
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === "string"
        ? (config[label]?.label ?? label)
        : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn("font-medium", labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    if (!value) {
      return null;
    }

    return <div className={cn("font-medium", labelClassName)}>{value}</div>;
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
    nameKey,
    compact,
  ]);

  if (!active || !payload?.length) {
    return null;
  }

  if (compact) {
    const rows = payload.filter((item) => item.type !== "none");
    return (
      <div className={cn("flex min-w-0 flex-col gap-1 text-[10px]", className)}>
        {rows.map((item, index) => {
          const datumKey = resolveChartConfigDatumKey(item, nameKey);
          const itemConfig = getPayloadConfigFromPayload(
            config,
            item,
            datumKey,
          );
          const indicatorColor =
            color ??
            (item.payload as { fill?: string } | undefined)?.fill ??
            item.color ??
            resolveChartColorFromConfig(itemConfig, datumKey);
          const segmentLabel = itemConfig?.label ?? item.name;
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: order matches Recharts payload order
              key={index}
              className="flex min-w-0 max-w-[min(100%,280px)] items-center gap-1.5 rounded-md bg-background px-2 py-1 shadow-md"
            >
              {!hideIndicator &&
                (itemConfig?.icon ? (
                  <itemConfig.icon />
                ) : (
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px] border border-solid"
                    style={{
                      ...(indicatorColor != null && indicatorColor !== ""
                        ? {
                            backgroundColor: indicatorColor,
                            borderColor: indicatorColor,
                          }
                        : undefined),
                    }}
                  />
                ))}
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2 leading-none">
                <span className="min-w-0 text-muted-foreground">
                  {segmentLabel}
                </span>
                {item.value != null && (
                  <span className="shrink-0 font-medium text-foreground tabular-nums">
                    {typeof item.value === "number"
                      ? item.value.toLocaleString()
                      : String(item.value)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const nestLabel = payload.length === 1 && indicator !== "dot";

  return (
    <div
      className={cn(
        "grid min-w-32 items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[10px] shadow-xl",
        className,
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {payload
          .filter((item) => item.type !== "none")
          .map((item, index) => {
            const datumKey = resolveChartConfigDatumKey(item, nameKey);
            const itemConfig = getPayloadConfigFromPayload(
              config,
              item,
              datumKey,
            );
            const indicatorColor =
              color ??
              (item.payload as { fill?: string } | undefined)?.fill ??
              item.color ??
              resolveChartColorFromConfig(itemConfig, datumKey);

            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: order matches Recharts payload order
                key={index}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                  indicator === "dot" && "items-center",
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            "shrink-0 rounded-[2px] border border-solid",
                            {
                              "h-2.5 w-2.5": indicator === "dot",
                              "w-1": indicator === "line",
                              "w-0 border-[1.5px] border-dashed":
                                indicator === "dashed",
                              "my-0.5": nestLabel && indicator === "dashed",
                            },
                          )}
                          style={{
                            ...(indicatorColor != null && indicatorColor !== ""
                              ? {
                                  backgroundColor:
                                    indicator === "dashed"
                                      ? "transparent"
                                      : indicatorColor,
                                  borderColor: indicatorColor,
                                }
                              : undefined),
                          }}
                        />
                      )
                    )}
                    <div
                      className={cn(
                        "flex flex-1 justify-between leading-none",
                        nestLabel ? "items-end" : "items-center",
                      )}
                    >
                      <div className="grid gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="text-muted-foreground">
                          {itemConfig?.label ?? item.name}
                        </span>
                      </div>
                      {item.value != null && (
                        <span className="font-mono font-medium text-foreground tabular-nums">
                          {typeof item.value === "number"
                            ? item.value.toLocaleString()
                            : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

const getPayloadConfigFromPayload = (
  config: ChartConfig,
  payload: unknown,
  key: string,
) => {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key];
};

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  getPayloadConfigFromPayload,
  resolveChartColorFromConfig,
  resolveChartConfigDatumKey,
  useChart,
};
